import { LoadedUser } from '../../interfaces';
import { GiftCardType } from '../../types/payments';
import { isUserAdminOrManager } from '../../utils/global/helper';
import { canIndependentManageShop } from '../businesses';

export function canManageGiftCards(user: LoadedUser | null | undefined) {
  return (
    !!user && (isUserAdminOrManager(user) || canIndependentManageShop(user))
  );
}

export function formatGiftCardCodeInput(value: string): string {
  // Remove all non-alphanumeric characters
  const cleaned = value.replace(/[^A-Z0-9]/gi, '').toUpperCase();
  // Add hyphens every 4 characters
  const formatted = cleaned.match(/.{1,4}/g)?.join('-') || cleaned;
  return formatted;
}

export function formatGiftCardCode(code: string): string {
  return code.replace(/(.{4})/g, '$1 ').trim();
}

export function getMaxAmountToUse({
  isSplitPayment,
  amountCents,
  giftCardBalanceCents,
}: {
  isSplitPayment: boolean;
  amountCents: number;
  giftCardBalanceCents: number;
}) {
  return isSplitPayment
    ? amountCents
    : Math.min(giftCardBalanceCents, amountCents);
}

export function validateGiftCardAmount({
  giftCardType,
  amountCents,
  giftCardBalanceCents,
  totalPriceCents,
  isSplitPayment,
}: {
  giftCardType: GiftCardType;
  amountCents: number;
  giftCardBalanceCents: number;
  totalPriceCents: number;
  isSplitPayment: boolean;
}): {
  isValid: boolean;
  error: string | null;
} {
  const isInternalChecked = giftCardType === 'internal';
  const maxAmountToUse = getMaxAmountToUse({
    isSplitPayment,
    amountCents,
    giftCardBalanceCents,
  });

  if (
    isInternalChecked &&
    maxAmountToUse < totalPriceCents &&
    !isSplitPayment
  ) {
    return {
      isValid: false,
      error:
        "The customer's gift card balance is less than the total amount of the payment. Use the split payment feature to pay the remaining amount.",
    };
  }

  if (isInternalChecked && amountCents > giftCardBalanceCents) {
    return {
      isValid: false,
      error:
        "The amount you are trying to use is greater than the customer's gift card balance.",
    };
  }

  return { isValid: true, error: null };
}
